import { useTranslation } from "react-i18next";

import { useState, useEffect, useMemo } from "react";

import { useMutation, useLazyQuery, useQuery } from "@apollo/client";

import Fieldset from "../../../Global/Forms/Fieldset";

import SelectSpread from "../../../Global/Forms/Select_Spread";

import { CONFIRM_DELIVERY, DELIVERY_LABEL, DELIVERY_LABELS, CREATE_COURIER_PICKUP, DELIVERY_PLANS } from "../../../../../Queries/Orders";

export default function DeliverySettings ( { state, update, orderType } ) { const

    { t: __ } = useTranslation (),

    [ deliveryPrice, setDeliveryPrice ] = useState ( state.deliveryPrice ),

    [ deliveryId, setDeliveryId ] = useState ( state.deliveryPlanId || null ),
    
    [ initialized, setInitialized ] = useState ( false ),

    [ originalDeliveryPlanId, setOriginalDeliveryPlanId ] = useState ( state.deliveryPlanId ? parseInt ( state.deliveryPlanId ) : null ),

    [ deliveryError, setDeliveryError ] = useState ( null ),

    [ confirmDelivery, { loading: confirmLoading, error: confirmError } ] = useMutation ( CONFIRM_DELIVERY, {
        onCompleted: ( data ) => {
            const response = data.adminConfirmDelivery;
            
            // Get tracking IDs from order object or mutation response
            const trackingIds = response.order?.trackingIds || response.trackingIds || [];
            
            if ( response.order ) {
                // Success - update state and clear error
                update ( prev => ( {
                    ...prev,
                    deliveryConfirmedAt: response.order.deliveryConfirmedAt,
                    trackingIds: trackingIds,
                    deliveryPlanId: response.order.deliveryPlanId || prev.deliveryPlanId,
                    deliveryMethod: response.order.deliveryMethod || prev.deliveryMethod,
                    deliveryPrice: response.order.deliveryPrice ?? prev.deliveryPrice,
                    customDeliveryPrice: response.order.deliveryPrice ?? prev.deliveryPrice
                } ) );

                setDeliveryPrice ( response.order.deliveryPrice ?? state.deliveryPrice );

                setOriginalDeliveryPlanId ( response.order.deliveryPlanId || state.deliveryPlanId );
                setDeliveryError ( null );
            }
            
            if ( response.errors?.length > 0 ) {
                // Error from backend - display message and revert selection
                setDeliveryError ( response.errors.join ( "\n" ) );
                // Revert to original delivery plan
                if ( originalDeliveryPlanId ) {
                    setDeliveryId ( originalDeliveryPlanId );
                }
            }
        },
        onError: ( error ) => {
            setDeliveryError ( error.message );
            // Revert to original delivery plan
            if ( originalDeliveryPlanId ) {
                setDeliveryId ( originalDeliveryPlanId );
            }
        }
    } ),

    /**
     * Fetch all delivery plans as fallback for new orders (when availableDeliveryMethods is empty)
     */
    { data: deliveryPlansData } = useQuery ( DELIVERY_PLANS ),

    /**
     * Available delivery methods for this order from backend
     * Based on order country, products, etc.
     * For new orders (no state.id), fallback to DELIVERY_PLANS query
     * @see tireshop_shared/app/services/delivery/manager.rb
     */
    availableDeliveryMethods = useMemo ( () => {

        if ( state.availableDeliveryMethods && state.availableDeliveryMethods.length > 0 ) {
            return state.availableDeliveryMethods;
        }

        if ( ! state.id && deliveryPlansData?.deliveryPlans ) {
            return deliveryPlansData.deliveryPlans
                .filter ( plan => plan.enabled )
                .map ( plan => ( {
                    planId: plan.planId,
                    name: plan.name || plan.value,
                    deliveryPlanId: parseInt ( plan.id ),
                    approximatePrice: 0,
                    preciseAdressRequired: false,
                    supportsCourierPickup: false
                } ) );
        }

        return [];

    }, [ state.availableDeliveryMethods, state.id, deliveryPlansData ] ),

    /**
     * Get the current delivery method details for display
     */
    currentDeliveryMethod = useMemo ( () => {

        const planKey = state.deliveryPlanId || deliveryId;

        if ( ! availableDeliveryMethods.length || planKey === null || planKey === undefined || planKey === "" ) return null;

        return availableDeliveryMethods.find ( 
            method => parseInt ( method.deliveryPlanId, 10 ) === parseInt ( planKey, 10 ) 
        );

    }, [ availableDeliveryMethods, state.deliveryPlanId, deliveryId ] ),

    handleConfirmDelivery = () => {

        if ( ! state.id ) return;

        confirmDelivery ( {
            variables: {
                orderId: state.id,
                deliveryPlanId: deliveryId ? String ( deliveryId ) : null,
                deliveryPrice: parseFloat ( deliveryPrice ) || 0
            }
        } );

    },

    /**
     * Check if delivery plan supports printable labels (Omniva services)
     */
    supportsPrintableLabels = useMemo ( () => {

        if ( ! currentDeliveryMethod ) return false;
        const planId = currentDeliveryMethod.planId;
        return planId === "omniva_courier" || planId === "omniva_parcelbox";

    }, [ currentDeliveryMethod ] ),

    /**
     * Check if delivery plan supports courier pickup (from backend)
     */
    supportsCourierPickup = useMemo ( () => {

        if ( ! currentDeliveryMethod ) return false;
        return currentDeliveryMethod.supportsCourierPickup === true;

    }, [ currentDeliveryMethod ] ),

    /**
     * Delivery cost cannot be changed when order is paid or awaiting payment
     */
    deliveryCostLocked = useMemo ( () => {

        const s = ( state.status || "" ).toLowerCase ();
        return s === "payed" || s === "awaiting_payment";

    }, [ state.status ] ),

    [ fetchLabel, { loading: labelLoading } ] = useLazyQuery ( DELIVERY_LABEL, {
        onCompleted: ( data ) => {
            if ( data.deliveryLabel?.success && data.deliveryLabel?.pdfData ) {
                const pdfData = data.deliveryLabel.pdfData;
                const filename = data.deliveryLabel.filename || "delivery_label.pdf";
                
                try {
                    const byteCharacters = atob ( pdfData );
                    const byteNumbers = new Array ( byteCharacters.length );
                    for ( let i = 0; i < byteCharacters.length; i++ ) {
                        byteNumbers[ i ] = byteCharacters.charCodeAt ( i );
                    }
                    const byteArray = new Uint8Array ( byteNumbers );
                    const blob = new Blob ( [ byteArray ], { type: "application/pdf" } );
                    
                    const url = window.URL.createObjectURL ( blob );
                    const link = document.createElement ( "a" );
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild ( link );
                    link.click ();
                    document.body.removeChild ( link );
                    window.URL.revokeObjectURL ( url );
                } catch ( error ) {
                    alert ( __ ( "error_downloading_label" ) + ": " + error.message );
                }
            } else {
                alert ( data.deliveryLabel?.error || __ ( "error_fetching_label" ) );
            }
        },
        onError: ( error ) => {
            alert ( __ ( "error_fetching_label" ) + ": " + error.message );
        }
    } ),

    handleLabelClick = ( trackingCode ) => {
        if ( ! state.id || ! trackingCode || ! supportsPrintableLabels ) return;
        fetchLabel ( { variables: { orderId: state.id, trackingCode: trackingCode } } );
    },

    [ fetchAllLabels, { loading: allLabelsLoading } ] = useLazyQuery ( DELIVERY_LABELS, {
        onCompleted: ( data ) => {
            if ( data.deliveryLabels?.success && data.deliveryLabels?.pdfData ) {
                const pdfData = data.deliveryLabels.pdfData;
                const filename = data.deliveryLabels.filename || "delivery_labels.pdf";
                
                try {
                    const byteCharacters = atob ( pdfData );
                    const byteNumbers = new Array ( byteCharacters.length );
                    for ( let i = 0; i < byteCharacters.length; i++ ) {
                        byteNumbers[ i ] = byteCharacters.charCodeAt ( i );
                    }
                    const byteArray = new Uint8Array ( byteNumbers );
                    const blob = new Blob ( [ byteArray ], { type: "application/pdf" } );
                    
                    const url = window.URL.createObjectURL ( blob );
                    const link = document.createElement ( "a" );
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild ( link );
                    link.click ();
                    document.body.removeChild ( link );
                    window.URL.revokeObjectURL ( url );
                } catch ( error ) {
                    alert ( __ ( "error_downloading_label" ) + ": " + error.message );
                }
            } else {
                alert ( data.deliveryLabels?.error || __ ( "error_fetching_label" ) );
            }
        },
        onError: ( error ) => {
            alert ( __ ( "error_fetching_label" ) + ": " + error.message );
        }
    } ),

    handlePrintAll = () => {
        if ( ! state.id || ! supportsPrintableLabels || ! state.trackingIds?.length ) return;
        fetchAllLabels ( { variables: { orderId: state.id } } );
    },

    [ createCourierPickup, { loading: pickupLoading } ] = useMutation ( CREATE_COURIER_PICKUP, {
        onCompleted: ( data ) => {
            if ( data.adminCreateCourierPickup?.success ) {
                alert ( data.adminCreateCourierPickup.message || __ ( "courier_pickup_scheduled" ) );
            } else {
                alert ( data.adminCreateCourierPickup?.error || __ ( "error_creating_pickup" ) );
            }
        },
        onError: ( error ) => {
            alert ( __ ( "error_creating_pickup" ) + ": " + error.message );
        }
    } ),

    handleCallCourier = () => {
        if ( ! state.id || ! supportsCourierPickup ) return;
        
        // Default to tomorrow, 9:00-17:00
        const tomorrow = new Date ();
        tomorrow.setDate ( tomorrow.getDate () + 1 );
        const pickupDate = tomorrow.toISOString ().split ( 'T' )[ 0 ];
        
        createCourierPickup ( {
            variables: {
                orderId: state.id,
                pickupDate: pickupDate,
                pickupTimeFrom: "09:00",
                pickupTimeTo: "17:00",
                comment: state.orderDeliveryNotes || `Order #${ state.id }`
            }
        } );
    },

    /**
     * Keep order state (and order price calculator in Products) in sync when the admin edits delivery price.
     * Uses functional update so the value is correct while typing and matches Edit.js effective delivery logic.
     */

    handleDeliveryPriceChange = e => {

        const raw = e.target.value;

        setDeliveryPrice ( raw );

        update ( prev => {

            const num = parseFloat ( raw );

            const resolved = ( raw !== "" && ! Number.isNaN ( num ) )
                ? num
                : ( prev.deliveryPrice ?? 0 );

            return {
                ...prev,
                customDeliveryPrice: resolved,
                deliveryPrice: resolved,
                deliveryPriceManuallyEdited: true
            };

        } );

    };

    /**
     * 
     * @event useEffect
     * 
     * @description Initialize deliveryId when available delivery methods are loaded.
     * Always defaults to "tireshop_pickup" if no delivery plan is selected.
     * Works for both new orders (using DELIVERY_PLANS) and existing orders (using availableDeliveryMethods).
     * 
     */

    useEffect ( () => {

        if ( availableDeliveryMethods.length > 0 && ! initialized ) {

            const pickupMethod = availableDeliveryMethods.find ( 
                el => el.planId === "tireshop_pickup" 
            );

            let selectedMethod = null;


            if ( state.deliveryPlanId ) {

                const existingMethod = availableDeliveryMethods.find (
                    el => parseInt ( el.deliveryPlanId ) === parseInt ( state.deliveryPlanId )
                );

                if ( existingMethod ) {
                    selectedMethod = existingMethod;
                    setDeliveryId ( parseInt ( state.deliveryPlanId ) );
                } else if ( pickupMethod ) {
                    selectedMethod = pickupMethod;
                    setDeliveryId ( parseInt ( pickupMethod.deliveryPlanId ) );
                    // Update state with pickup method if it wasn't set
                    update ( prev => ( {
                        ...prev,
                        deliveryPlanId: parseInt ( pickupMethod.deliveryPlanId ),
                        deliveryPrice: pickupMethod.approximatePrice || 0
                    } ) );
                } else {
                    selectedMethod = availableDeliveryMethods[ 0 ];
                    setDeliveryId ( parseInt ( availableDeliveryMethods[ 0 ].deliveryPlanId ) );
                    // Update state with first method if it wasn't set
                    update ( prev => ( {
                        ...prev,
                        deliveryPlanId: parseInt ( availableDeliveryMethods[ 0 ].deliveryPlanId ),
                        deliveryPrice: availableDeliveryMethods[ 0 ].approximatePrice || 0
                    } ) );
                }

            } else {
                if ( pickupMethod ) {
                    selectedMethod = pickupMethod;
                    setDeliveryId ( parseInt ( pickupMethod.deliveryPlanId ) );
                    // Update state with pickup method
                    update ( prev => ( {
                        ...prev,
                        deliveryPlanId: parseInt ( pickupMethod.deliveryPlanId ),
                        deliveryPrice: pickupMethod.approximatePrice || 0
                    } ) );
                } else {
                    selectedMethod = availableDeliveryMethods[ 0 ];
                    setDeliveryId ( parseInt ( availableDeliveryMethods[ 0 ].deliveryPlanId ) );
                    // Update state with first method
                    update ( prev => ( {
                        ...prev,
                        deliveryPlanId: parseInt ( availableDeliveryMethods[ 0 ].deliveryPlanId ),
                        deliveryPrice: availableDeliveryMethods[ 0 ].approximatePrice || 0
                    } ) );
                }
            }

            if ( selectedMethod ) {
                const calculatedPrice = selectedMethod.approximatePrice || 0;
                const manualPrice = state.deliveryPrice != null && state.deliveryPrice !== "" ? parseFloat ( state.deliveryPrice ) : ( state.customDeliveryPrice != null && state.customDeliveryPrice !== "" ? parseFloat ( state.customDeliveryPrice ) : null );
                const priceToUse = ( manualPrice != null && ! Number.isNaN ( manualPrice ) ) ? manualPrice : calculatedPrice;
                setDeliveryPrice ( priceToUse );
                // Set original delivery plan ID on initialization
                if ( ! originalDeliveryPlanId ) {
                    setOriginalDeliveryPlanId ( parseInt ( selectedMethod.deliveryPlanId ) );
                }
            }

            setInitialized ( true );

        }

    }, [ availableDeliveryMethods, state.deliveryPlanId, orderType, initialized, originalDeliveryPlanId ] );

    /**
     * 
     * @event useEffect
     * 
     * @description Sync deliveryId when state.deliveryPlanId changes externally.
     * 
     */

    useEffect ( () => {

        if ( initialized && state.deliveryPlanId && parseInt ( state.deliveryPlanId ) !== parseInt ( deliveryId ) ) {

            setDeliveryId ( parseInt ( state.deliveryPlanId ) );

        }

    }, [ state.deliveryPlanId ] );

    /**
     * Mirror state.deliveryPrice back into the input when it changes from elsewhere
     * (e.g. calculator sync in Products.js), but never overwrite while the admin is
     * actively typing (deliveryPriceManuallyEdited flag).
     */

    useEffect ( () => {

        if ( state.deliveryPriceManuallyEdited ) return;

        if ( state.deliveryPrice == null ) return;

        const next = parseFloat ( state.deliveryPrice );

        if ( Number.isNaN ( next ) ) return;

        const current = parseFloat ( deliveryPrice );

        if ( ! Number.isNaN ( current ) && Math.abs ( current - next ) < 0.005 ) return;

        setDeliveryPrice ( next );

    }, [ state.deliveryPrice, state.deliveryPriceManuallyEdited ] );

    /**
     * 
     * @event useEffect
     * 
     * @description If deliveryId changes (user selection), update state with delivery method and price.
     * The approximatePrice is calculated by Delivery::Manager on the backend.
     * 
     */

    useEffect ( () => {

        if ( availableDeliveryMethods.length > 0 && initialized && deliveryId ) {

            const selectedMethod = availableDeliveryMethods.find ( 
                method => parseInt ( method.deliveryPlanId ) === parseInt ( deliveryId ) 
            );

            if ( selectedMethod ) {

                const calculatedPrice = selectedMethod.approximatePrice || 0;
                const manualPrice = state.deliveryPrice != null && state.deliveryPrice !== "" ? parseFloat ( state.deliveryPrice ) : ( state.customDeliveryPrice != null && state.customDeliveryPrice !== "" ? parseFloat ( state.customDeliveryPrice ) : null );
                const priceToUse = ( manualPrice != null && ! Number.isNaN ( manualPrice ) ) ? manualPrice : calculatedPrice;

                setDeliveryPrice ( priceToUse );

                update ( prev => ( {
                    ...prev,
                    deliveryPlanId: parseInt ( deliveryId ),
                    deliveryPrice: priceToUse,
                    customDeliveryPrice: priceToUse,
                    deliveryPriceManuallyEdited: false
                } ) );

            }

        }

    }, [ deliveryId, initialized ] );

    /** @returns { JSX.Element } DeliverySettings */

    return (

        <Fieldset legend={ __ ( "delivery" ) }>

            { state.deliveryConfirmedAt && 
                <div style={{ 
                    padding: '8px', 
                    backgroundColor: '#d4edda', 
                    borderRadius: '4px', 
                    marginBottom: '8px',
                    fontSize: '12px',
                    color: '#155724'
                }}>
                    ✓ { __ ( "last_confirmed" ) }: { new Date ( state.deliveryConfirmedAt ).toLocaleString () }
                </div>
            }

            { deliveryError && 
                <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#f8d7da', 
                    borderRadius: '4px', 
                    marginBottom: '12px',
                    fontSize: '12px',
                    color: '#721c24',
                    border: '1px solid #f5c6cb',
                    whiteSpace: 'pre-wrap'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        ✕ { __ ( "delivery_error" ) }
                    </div>
                    { deliveryError }
                    <button 
                        type="button"
                        onClick={ () => setDeliveryError ( null ) }
                        style={{
                            marginTop: '8px',
                            padding: '4px 8px',
                            backgroundColor: 'transparent',
                            color: '#721c24',
                            border: '1px solid #721c24',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '11px'
                        }}
                    >
                        { __ ( "dismiss" ) }
                    </button>
                </div>
            }

                <SelectSpread 
            
                    id="deliveryId"

                i18n={ false }

                def={ false }
                
                action={ e => { setDeliveryId ( parseInt ( e.target.value ) ); setDeliveryError ( null ); } }
                
                options={ availableDeliveryMethods.map ( method => ( { 
                    value: parseInt ( method.deliveryPlanId ), 
                    name: method.name
                } ) ) }
                
                value={ parseInt ( deliveryId ) } />

            {

                currentDeliveryMethod &&
                
                <div className="delivery-info" style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    <div>
                        <strong>{ currentDeliveryMethod.name }</strong>
                        <span style={{ marginLeft: '8px', color: '#999' }}>
                            ({ currentDeliveryMethod.planId })
                        </span>
                    </div>
                    { currentDeliveryMethod.approximatePrice > 0 &&
                        <div style={{ marginTop: '4px' }}>
                            { __ ( "approximate_price" ) }: <strong>€{ currentDeliveryMethod.approximatePrice?.toFixed ( 2 ) }</strong>
                        </div>
                    }
                    { currentDeliveryMethod.preciseAdressRequired &&
                        <div style={{ marginTop: '4px', color: '#e67e22' }}>
                            ⚠ { __ ( "precise_address_required" ) }
                        </div>
                    }
                </div>

            }

            {

                state.trackingIds && state.trackingIds.length > 0 &&

                <div className="tracking-info" style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                        { __ ( "tracking_ids" ) }:
                    </div>
                    { state.trackingIds.map ( ( trackingCode, index ) => (
                        <div key={ index } style={{ fontSize: '12px', fontFamily: 'monospace', padding: '2px 0' }}>
                            { supportsPrintableLabels ? (
                                <span
                                    onClick={ () => handleLabelClick ( trackingCode ) }
                                    style={{
                                        cursor: labelLoading ? 'wait' : 'pointer',
                                        color: '#007bff',
                                        textDecoration: 'underline',
                                        userSelect: 'none'
                                    }}
                                    title={ __ ( "click_to_download_label" ) }
                                >
                                    { trackingCode }
                                    { labelLoading && <span style={{ marginLeft: '4px' }}>...</span> }
                                </span>
                            ) : (
                                <span>{ trackingCode }</span>
                            ) }
                        </div>
                    ) ) }
                    { supportsPrintableLabels && state.trackingIds.length > 0 &&
                        <button
                            type="button"
                            onClick={ handlePrintAll }
                            disabled={ allLabelsLoading }
                            style={{
                                marginTop: '8px',
                                padding: '6px 12px',
                                backgroundColor: '#6f42c1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: allLabelsLoading ? 'not-allowed' : 'pointer',
                                opacity: allLabelsLoading ? 0.7 : 1,
                                width: '100%',
                                fontSize: '13px'
                            }}
                        >
                            { allLabelsLoading ? __ ( "downloading" ) + "..." : __ ( "print_all_labels" ) }
                        </button>
                    }
                </div>

            }

            {
                /* Delivery price input: show when a method is selected so admin can change and save with the order */
                currentDeliveryMethod &&

                <div className="input-group default" style={{ marginTop: '8px' }}>

                    <label htmlFor="deliveryPrice" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        { __ ( "delivery_price" ) }
                    </label>

                    { deliveryCostLocked &&
                        <div style={{ fontSize: '11px', color: '#856404', backgroundColor: '#fff3cd', padding: '6px 8px', borderRadius: '4px', marginBottom: '8px' }}>
                            { __ ( "delivery_cost_locked" ) }
                        </div>
                    }

                    <input

                        type="number"

                        id="deliveryPrice"

                        value={ deliveryPrice }

                        onChange={ handleDeliveryPriceChange }

                        step="0.01"

                        min="0"

                        disabled={ deliveryCostLocked }

                    />

                </div>

            }

            <textarea
            
                name="orderDeliveryNotes"

                id="orderDeliveryNotes"

                onChange={ e => update ( prev => ( { ...prev, orderDeliveryNotes: e.target.value } ) ) }

                placeholder={ `${ __ ( "delivery_notes" ) }...` }

                value={ state.orderDeliveryNotes }
            
            />

            { supportsCourierPickup && state.id &&
                <button
                    type="button"
                    onClick={ handleCallCourier }
                    disabled={ pickupLoading }
                    style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: pickupLoading ? 'not-allowed' : 'pointer',
                        opacity: pickupLoading ? 0.7 : 1,
                        width: '100%',
                        fontSize: '14px'
                    }}
                >
                    { pickupLoading ? __ ( "scheduling" ) + "..." : __ ( "call_courier" ) }
                </button>
            }

            { state.id &&
                <button
                    type="button"
                    onClick={ handleConfirmDelivery }
                    disabled={ confirmLoading }
                    style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: confirmLoading ? 'not-allowed' : 'pointer',
                        opacity: confirmLoading ? 0.7 : 1,
                        width: '100%',
                        fontSize: '14px'
                    }}
                >
                    { confirmLoading ? __ ( "confirming" ) + "..." : ( state.deliveryConfirmedAt ? __ ( "update_delivery" ) : __ ( "confirm_delivery" ) ) }
                </button>
            }

        </Fieldset>

    );

};
